import { Injectable, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { FilterFieldGroup, FilterMenuOptions, Project } from '../../models';
import { TOOLTIP_CONSTANTS } from '../../shared/constants';
import { FieldTypes } from '../logic-services';
import { Field, FilterMenuServiceModel } from '../logic-services/models';

@Injectable({
    providedIn: 'root'
})
export class FilterMenuFormUtilityService {

    constructor(
        private _formBuilder: UntypedFormBuilder,
    ) { }

    dataChanges(changes: SimpleChanges, inputtedFilterMenuOptions: FilterMenuOptions, model: FilterMenuServiceModel, changePropertyName = 'dashboardFilterMenuOptions'): void {
        if (changes[changePropertyName]?.currentValue) {
            if (JSON.stringify(model.filterMenuOptions.allFields) !== JSON.stringify(inputtedFilterMenuOptions.allFields) ||
                JSON.stringify(model.filterMenuOptions.filterValues) !== JSON.stringify(inputtedFilterMenuOptions.filterValues)) {
                model.filterMenuOptions = inputtedFilterMenuOptions;
                model.form = this.createForm(model);
            }
        }
    }

    createForm(model: FilterMenuServiceModel): UntypedFormGroup {
        const group = this._formBuilder.group({});
        model.filterMenuOptions.allFields.forEach(field => {
            let formArray = this._formBuilder.array([]);

            if (field?.FieldType?.text === FieldTypes.DateSelect) {
                formArray = new UntypedFormArray([new UntypedFormControl(null), new UntypedFormControl(null)]);
                group.addControl(field.id, formArray);
                return;
            }

            if (!model.filterMenuOptions.filterValues[field.id]) {
                return;
            }
            model.filterMenuOptions.filterValues[field.id].forEach(() => {
                formArray.push(new UntypedFormControl(false));
            });
            group.addControl(field.id, formArray);
        });
        return group;
    }

    getFilteredMenuOptionsForSites(model: FilterMenuServiceModel): FilterMenuOptions {
        const filterValues = { ...model.filterMenuOptions.filterValues };
        let selectedFields = [...model.filterMenuOptions.selectedFields];

        for (const fieldId in model.form.value) {
            const formArrayValues = model.form.value[fieldId];
            const originalValues = filterValues[fieldId];
            const fieldType = model.filterMenuOptions.allFields.find(field => field.id === fieldId)?.FieldType?.text;
            if (fieldType === FieldTypes.DateSelect) {
                filterValues[fieldId] = formArrayValues.filter((value: any) => value !== null);
            }
            else {
                filterValues[fieldId] = originalValues.filter((_, index) => formArrayValues[index]);
            }

            if (filterValues[fieldId].length === 0) {
                delete filterValues[fieldId];
                selectedFields = selectedFields.filter(field => field.id !== fieldId);
            }
        }

        const filterForm = new FilterMenuOptions();
        filterForm.filterValues = filterValues;
        filterForm.selectedFields = selectedFields;
        return filterForm;
    }

    groupFieldsBySourceProject(fields: Field[], currentProject: Project | undefined, projects: Project[]): FilterFieldGroup[] {
        const allFields: Field[] = fields ?? [];
        if (!currentProject?.isSummaryProject) {
            const singleGroup = new FilterFieldGroup();
            singleGroup.fields = allFields;
            return allFields.length > 0 ? [singleGroup] : [];
        }

        // Site fields have no source project, so they come first without a project label.
        const siteFieldsGroup = new FilterFieldGroup();
        const groupsByProjectId = new Map<string, FilterFieldGroup>();
        allFields.forEach((field: Field) => {
            if (!field.projectId) {
                siteFieldsGroup.fields.push(field);
                return;
            }

            let projectGroup: FilterFieldGroup | undefined = groupsByProjectId.get(field.projectId);
            if (!projectGroup) {
                projectGroup = new FilterFieldGroup();
                projectGroup.projectId = field.projectId;
                projectGroup.projectName = projects.find((proj: Project) => proj.id === field.projectId)?.name;
                groupsByProjectId.set(field.projectId, projectGroup);
            }

            projectGroup.fields.push(field);
        });

        return [siteFieldsGroup, ...groupsByProjectId.values()].filter((group: FilterFieldGroup) => group.fields.length > 0);
    }

    getSourceProjectTooltip(field: Field, currentProject: Project | undefined, projects: Project[], projectAlias: string): string | null {
        if (!currentProject?.isSummaryProject || !field.projectId) {
            return null;
        }

        const sourceProjectName = projects.find((proj: Project) => proj.id === field.projectId)?.name;

        return sourceProjectName ? TOOLTIP_CONSTANTS.MAP_FILTER_SOURCE_PROJECT_TOOLTIP(projectAlias, sourceProjectName) : null;
    }
}
